import type { BoardAction, BoardState, GridConfig } from './types';

/**
 * Pure board state machine. Ported from the CreateJS engine's
 * `tradePiece()` / `correctAnswer()` / `checkForBonus()` / `incorrectAnswer()`
 * (`games/engine/boardGameEngine.ts:764-815`,
 * `games/engine/rulesBoardEngine.ts:110-162`).
 *
 * Deliberately preserved legacy behaviour:
 *
 * - A drop is a **swap**, never a placement. Every cell always holds a value;
 *   there is no empty state and no answer tray.
 * - A correct drop can score **two** cells: the target, plus the origin if the
 *   value swapped back into it happens to be correct there (the bonus rule).
 * - A wrong drop rejects the **whole board**, not just the offending cell.
 *   The caller re-deals via `DEAL` once the rejection animation finishes.
 *
 * One intentional simplification: the legacy `tradePiece` sets
 * `tileLocked[to] = 1` before it checks correctness, so a wrong drop briefly
 * marks its target locked. That is unobservable - the wrong branch disables
 * the board and re-deals immediately - so `locked` here means strictly
 * "resolved correctly", which is what the renderer needs for styling.
 */
export function boardReducer(
  state: BoardState,
  action: BoardAction,
): BoardState {
  switch (action.type) {
    case 'DEAL':
      return createBoardState(state.config, action);

    case 'DROP':
      return applyDrop(state, action.from, action.to);

    default:
      return state;
  }
}

/** Builds a fresh board from a deal. Also the reducer's `DEAL` handler. */
export function createBoardState(
  config: GridConfig,
  deal: {
    equations: readonly string[];
    expected: readonly string[];
    placed: readonly string[];
  },
): BoardState {
  return {
    config,
    equations: deal.equations,
    expected: deal.expected,
    placed: deal.placed,
    locked: deal.placed.map(() => false),
    correctCount: 0,
    complete: false,
    rejectingIndex: null,
  };
}

function applyDrop(state: BoardState, from: number, to: number): BoardState {
  if (!canDrop(state, from, to)) {
    return state;
  }

  const placed = [...state.placed];
  const swapped = placed[to]!;
  placed[to] = placed[from]!;
  placed[from] = swapped;

  // Legacy `tradePiece`: the target is judged against its own expected value.
  if (placed[to] !== state.expected[to]) {
    return { ...state, placed, rejectingIndex: to };
  }

  const locked = [...state.locked];
  locked[to] = true;
  let correctCount = state.correctCount + 1;

  // Legacy `checkForBonus`: the value swapped back into the origin may also
  // be correct there, resolving two cells from a single drag.
  if (!locked[from] && placed[from] === state.expected[from]) {
    locked[from] = true;
    correctCount += 1;
  }

  return {
    ...state,
    placed,
    locked,
    correctCount,
    complete: correctCount === state.config.rows * state.config.cols,
  };
}

/** What a drop resolved, for the caller to react to (sounds, clock, scoring). */
export interface DropOutcome {
  /** Newly locked indices, target first then origin (the bonus rule). */
  solved: number[];
  rejected: boolean;
  complete: boolean;
}

/**
 * Pure counterpart to dispatching `DROP`: reports what the drop resolved to
 * without requiring the caller to diff two `BoardState`s itself.
 */
export function dropOutcome(
  state: BoardState,
  from: number,
  to: number,
): DropOutcome | null {
  if (!canDrop(state, from, to)) {
    return null;
  }

  const next = applyDrop(state, from, to);

  const solved: number[] = [];
  if (next.rejectingIndex === null) {
    if (next.locked[to] && !state.locked[to]) {
      solved.push(to);
    }
    if (next.locked[from] && !state.locked[from]) {
      solved.push(from);
    }
  }

  return {
    solved,
    rejected: next.rejectingIndex !== null,
    complete: next.complete,
  };
}

/**
 * Whether a drop is allowed. Mirrors the legacy guards in
 * `onBoardMouseDown` (`!tileLocked[row][col]`) and `onBoardMouseUp`
 * (`boardGameEngine.ts:441-469`), where dropping on the origin cell or onto
 * an already-locked cell releases the piece without trading.
 */
export function canDrop(state: BoardState, from: number, to: number): boolean {
  return (
    state.rejectingIndex === null &&
    !state.complete &&
    from !== to &&
    isLiveCell(state, from) &&
    isLiveCell(state, to)
  );
}

function isLiveCell(state: BoardState, index: number): boolean {
  return (
    Number.isInteger(index) &&
    index >= 0 &&
    index < state.placed.length &&
    !state.locked[index]
  );
}
